import { v4 as uuidv4 } from 'uuid';
import Container from '../ioc-container';
import { injectNlpConfig, parseSentence } from '../parser/parser';
import evaluate from 'static-eval';
import { parse } from 'esprima';
import moment from 'moment';
import nlp from 'compromise';
import { LocalisationTimeFormats } from '~/_base/help/localisationHelper';

const botStateConstuctors = {};

const commonSubstitutions = {
  poor: 'pour',
  'back hoe': 'backhoe',
  swims: 'SWMS',
  'pro cool': 'Procore',
  'pro core': 'Procore',
  protocol: 'Procore',
  croker: 'Procore',
  pranchal: 'Procore',
  proco: 'Procore',
  rochel: 'Procore',
  'brough will': 'Procore',
  poco: 'Procore',
  coco: 'Procore',
  particle: 'Procore',
  'but yet': 'budget',
  'but you': 'budget',
  fidget: 'budget',
  legit: 'budget',
  'but cheap': 'budget',
};
const commonSubstitutionsRegex = new RegExp(
  '\\b' + Object.keys(commonSubstitutions).join('\\b|\\b') + '\\b',
  'gi'
);

export default {
  startChatSession: startChatSession,
  preprocessChatPhrase: preprocessChatPhrase,
  processChatPhrase: processChatPhrase,
  updateChatSessionContext: updateChatSessionContext,
};

function startChatSession(id, options) {
  let { nlpConfig } = options;
  let session = {
    id: id || uuidv4(),
    nlpConfig: nlpConfig,
    history: [],
    context: {},
    isNew: true,
  };
  let sessionProvider = Container.get('sessionProvider');
  sessionProvider.persist(session.id, session);
  return session.id;
}

function updateChatSessionContext(sessionId, context) {
  let sessionProvider = Container.get('sessionProvider');
  let session = sessionProvider.restore(sessionId);
  session.context = context || {};
  sessionProvider.persist(sessionId, session);
}

function preprocessChatPhrase(sessionId, phrase) {
  return phrase.replace(
    commonSubstitutionsRegex,
    subPhrase => commonSubstitutions[subPhrase.toLowerCase()]
  );
}

function processChatPhrase(sessionId, phrase, correlationId) {
  correlationId = correlationId || uuidv4();

  let sessionProvider = Container.get('sessionProvider');
  let session = sessionProvider.restore(sessionId);
  injectNlpConfig(session['nlpConfig']);

  try {
    let botStateEntry =
      session.history.length > 0
        ? session.history[session.history.length - 1]
        : { class: 'BlankBotState' };
    let botState = botStateConstuctors[botStateEntry.class](
      session,
      botStateEntry
    );
    botState = botState.processChatPhrase(phrase);

    let isNewSession = session.isNew === true;
    session.isNew = false;
    sessionProvider.persist(sessionId, session);

    return botState.bubble().then(bubbles => {
      let otherBubbles = [];
      if (
        botStateEntry.sessionContext == null ||
        botStateEntry.sessionContext.project != session.context.project ||
        isNewSession
      ) {
        if (session.context.project) {
          otherBubbles.push({
            owner: 'system',
            text: `${session.context.projectJobNumber} - ${session.context.projectName}`,
          });
        } else {
          otherBubbles.push({ owner: 'system', text: `All Projects` });
        }
      }
      otherBubbles.push({ owner: 'user', text: phrase, state: botState.state });
      bubbles = otherBubbles.concat(bubbles);
      bubbles.forEach(bubble =>
        Object.assign(bubble, { correlation_id: correlationId })
      );
      postChatHistory(bubbles);
      return bubbles;
    });
  } catch (_) {
    let bubbles = [];
    bubbles.push({
      owner: 'user',
      text: phrase,
      state: { class: 'BlankBotState', error: `${_}` },
    });
    bubbles.push({
      owner: 'bot',
      text:
        'Oops something has gone wrong maybe you could try rephrasing the question.',
    });
    bubbles.forEach(bubble =>
      Object.assign(bubble, { correlation_id: correlationId })
    );
    postChatHistory(bubbles);
    return Promise.resolve(bubbles);
  }
}

function postChatHistory(bubbles) {
  let fetch = Container.get('fetch');
  let historyBubbles = bubbles.map(bubble => {
    let { state, ...messageProps } = bubble;
    return {
      message: messageProps,
      bot_state: JSON.stringify(state),
    };
  });
  fetch('/site_assistant', {
    method: 'POST',
    body: JSON.stringify({ messages: historyBubbles }),
  });
}

function formatDecimal(value) {
  if (Math.abs(value - Math.trunc(value)) > 0) {
    let precision = 2;
    while (value.toFixed(precision) == 0) {
      precision = precision + 1;
    }
    return value.toFixed(precision);
  }
  return parseInt(value);
}

class BotState {
  constructor(name, session, state) {
    this.name = name;
    this.session = session;
    this.state = state;
    if (this.state && !this.session.history.includes(this.state)) {
      this.state.class = this.name;
      this.state.sessionContext = session.context;
      this.session.history.push(this.state);
    }
  }

  processChatPhrase(sentence) {
    let parsedSentence = parseSentence(sentence);
    let lowConfidenceBotStates = this.lowConfidenceBotStates();
    for (let i = 0; i < lowConfidenceBotStates.length; i++) {
      let botState = lowConfidenceBotStates[i].tryCreate(
        this.session,
        parsedSentence
      );
      if (botState != null) {
        return botState;
      }
    }
    if (parsedSentence.confidence < 0.9) {
      return BlankBotState.tryCreate(this.session, parsedSentence);
    }
    return (
      this.next(parsedSentence) ||
      BlankBotState.tryCreate(this.session, parsedSentence)
    );
  }

  next(parsedSentence) {
    let nextBotStates = this.nextBotStates(parsedSentence);
    for (let i = 0; i < nextBotStates.length; i++) {
      let botState = nextBotStates[i].tryCreate(this.session, parsedSentence);
      if (botState != null) {
        return botState;
      }
    }
    return null;
  }

  lowConfidenceBotStates() {
    return [HelperBotState, CalculatorBotState];
  }

  nextBotStates(parsedSentence) {
    return [
      UnitConversionBotState,
      SummaryBotState,
      ProcoreBotState,
      HasSubstanceBotState,
    ];
  }

  bubble() {
    throw 'Impliment Me';
  }

  _combineStates(newState) {
    return Object.assign({}, this.state, newState);
  }
}

botStateConstuctors['BlankBotState'] = (session, state) =>
  new BlankBotState(session, state);
class BlankBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    return new BlankBotState(session, {
      type: 'blank',
      parsed: parsedSentence,
    });
  }

  constructor(session, state) {
    super('BlankBotState', session, state);
  }

  bubble() {
    return Promise.resolve([
      { owner: 'bot', text: 'Sorry I am not sure what you are asking.' },
    ]);
  }
}

botStateConstuctors['HasSubstanceBotState'] = (session, state) =>
  new HasSubstanceBotState(session, state);
class HasSubstanceBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    //if there is an identifyable subject transition to this state
    if (parsedSentence.subject.success) {
      let type = HasSubstanceBotState.typeFromParsedSentence(parsedSentence);
      let state = {
        type: type,
        parsed: parsedSentence,
        subject: parsedSentence.subject,
        datespan: parsedSentence.datespan,
        dimension: parsedSentence.dimension,
        unit: parsedSentence.unit,
      };
      return new HasSubstanceBotState(session, state);
    }
  }

  static formatSubstance(substance) {
    let nlpSubstance = nlp(substance.substance);
    if (substance.amount == 1) {
      nlpSubstance.nouns().toSingular();
      nlpSubstance.toTitleCase();
      substance.substance = nlpSubstance.out('text');
    } else {
      nlpSubstance.nouns().toPlural();
      nlpSubstance.toTitleCase();
      substance.substance = nlpSubstance.out('text');
    }

    if (substance.uom) {
      if (substance.amount - Math.trunc(substance.amount) != 0) {
        substance.amount = substance.amount.toFixed(2);
      }
      return `${substance.substance} - ${substance.amount}${substance.uom}`;
    }
    return `${substance.amount} ${substance.substance}`;
  }

  static typeFromParsedSentence(parsedSentence) {
    let type =
      parsedSentence.records == true ? 'substance-records' : 'substance';
    type = parsedSentence.export == true ? 'substance-export' : type;
    switch (parsedSentence.average) {
      case 'daily average':
      case 'monthly average':
        type = `substance-${parsedSentence.average.replace(' ', '-')}`;
        break;
    }
    return type;
  }

  constructor(session, state) {
    super('HasSubstanceBotState', session, state);
  }

  _combineStates(newState) {
    if (
      this.state.response_type != 'ok' &&
      this.state.response_type != 'nodata'
    ) {
      let historyItems = Array.from(this.session.history).reverse();
      for (let i = 0; i < historyItems.length; i++) {
        let historyItem = historyItems[i];
        if (
          historyItem.class == 'HasSubstanceBotState' &&
          (historyItem.response_type == 'ok' ||
            historyItem.response_type == 'nodata')
        ) {
          return Object.assign({}, historyItem, newState);
        }
      }
    }
    return Object.assign({}, this.state, newState);
  }

  hasOnly(parsedSentence, props) {
    let allProps = ['subject', 'dimension', 'datespan', 'unit'];
    return allProps
      .filter(prop => !props.includes(prop))
      .every(prop => parsedSentence[prop].success == false);
  }

  next(parsedSentence) {
    if (
      this.state.type == 'substance-export' &&
      this.state.response_type == 'ok'
    ) {
      let state = this._combineStates({ parsed: parsedSentence });
      let exportStateType = {
        true: 'substance-export-confirmed',
        false: 'substance-export-cancelled',
      }[parsedSentence.confirmation];
      exportStateType = exportStateType || 'substance-export-indeterminate';

      if (
        exportStateType != 'substance-export-indeterminate' ||
        !parsedSentence.subject.success
      ) {
        state = Object.assign(state, { type: exportStateType });
        return new HasSubstanceBotState(this.session, state);
      }
    }

    let showAll = parsedSentence.subject.normal == 'all';
    if (showAll) {
      let state = this._combineStates({ parsed: parsedSentence });
      state = Object.assign(state, {
        type: 'substance',
        dimension: { success: false },
      });
      return new HasSubstanceBotState(this.session, state);
    }

    //try move around dimensions, datespans, units, records and averages views if subject not defined
    if (!parsedSentence.subject.success) {
      let state = this._combineStates({ parsed: parsedSentence });

      let hasDimension = parsedSentence.dimension.success;
      if (hasDimension) {
        state = Object.assign(state, { dimension: parsedSentence.dimension });
      }
      let hasDatespan = parsedSentence.datespan.success;
      if (hasDatespan) {
        state = Object.assign(state, { datespan: parsedSentence.datespan });
      }
      let hasUnit = parsedSentence.unit.success && !parsedSentence.unit.value;
      if (hasUnit) {
        state = Object.assign(state, { unit: parsedSentence.unit });
      }

      if (this.session.history.length > 0) {
        let last = this.session.history[this.session.history.length - 1];
        if (
          last.subject.success &&
          last.subject.normal == 'summary' &&
          hasDatespan
        ) {
          let summary = SummaryBotState.tryCreate(this.session, {
            subject: last.subject,
            datespan: parsedSentence.datespan,
          });
          if (summary) {
            return summary;
          }
        }
      }

      let averageDefined = parsedSentence.average != null;
      if (
        hasDimension ||
        hasDatespan ||
        hasUnit ||
        averageDefined ||
        parsedSentence.records == true ||
        parsedSentence.export == true
      ) {
        let type = HasSubstanceBotState.typeFromParsedSentence(parsedSentence);
        state = Object.assign(state, { type: type });
        return new HasSubstanceBotState(this.session, state);
      }
    }
    return super.next(parsedSentence);
  }

  bubble() {
    if (
      this.state.subject.success &&
      this.state.subject.normal == 'summary' &&
      !this.state.datespan.success
    ) {
      return Promise.resolve([
        {
          owner: 'bot',
          text: `What time span would you like to summarize?\nNext time you can just include the date in your question:\n* summary for last week\n* summary for this month\n* summary for ${new Date().getFullYear()}`,
        },
      ]);
    }

    let fetch = Container.get('fetch');

    let params = [];
    params.push(`substance=${this.state.subject.normal}`);
    if (this.state.subject.normalVariants.length > 0) {
      params.push(
        `substanceVariants=${this.state.subject.normalVariants.join('|')}`
      );
    }
    if (this.state.datespan.success) {
      params.push(`start=${this.state.datespan.start}`);
      params.push(`end=${this.state.datespan.end}`);
    }
    if (this.state.dimension.success) {
      params.push(`dimension=${this.state.dimension.normal}`);
    }
    if (this.state.unit.success) {
      params.push(`uom=${this.state.unit.uom}`);
    }
    if (this.session.context.project) {
      params.push(`project=${this.session.context.project}`);
    }

    switch (this.state.type) {
      case 'substance-export-confirmed':
        params.push(`verbose=true`);
        params.push(`include_desc=true`);
        return fetch(
          `/site_assistant/substance_export.json?${params.join('&')}`,
          { method: 'POST' }
        )
          .then(response => response.json())
          .then(response => {
            if (response.ok === true) {
              return [
                {
                  owner: 'bot',
                  text:
                    'Great I have sent the email, you should get it in a couple of minutes.',
                },
              ];
            }
            return [
              {
                owner: 'bot',
                text: 'Something went wrong could you pleae try again.',
              },
            ];
          });

      case 'substance-export-cancelled':
        return Promise.resolve([
          { owner: 'bot', text: "Ok I won't send you anything right now." },
        ]);

      case 'substance-export-indeterminate':
        return Promise.resolve([
          {
            owner: 'bot',
            text:
              "I couldn't figure out what you wanted me to do. You can ask to export again to give it another go.",
          },
        ]);

      case 'substance':
        params.push('generate_image=true');
        return fetch(`/site_assistant/substance.json?${params.join('&')}`)
          .then(response => response.json())
          .then(response => {
            this.state.response_type = response.response_type;
            if (
              response.response &&
              response.requested_dimension &&
              !response.response.dimension
            ) {
              let dateRange = this.getDateRange(response.response);
              return [
                {
                  owner: 'bot',
                  text: `I could not find *${response.requested_dimension}* in the *${response.response.substance}* data ${dateRange.prefix} ${dateRange.text}`,
                },
              ];
            }
            switch (response.response_type) {
              case 'ok':
                let bubbles = [];
                let dateRange = this.getDateRange(response.response);
                bubbles.push({
                  owner: 'bot',
                  text: `*Total*\n*${
                    dateRange.text
                  }*\n${HasSubstanceBotState.formatSubstance(
                    response.response
                  )}`,
                });
                if (response.response.image_uri) {
                  let image_bubble = {
                    owner: 'bot',
                    image_uri: response.response.image_uri,
                    actions: [
                      {
                        type: 'chat',
                        label: 'List records',
                        data: 'List records',
                      },
                      { type: 'chat', label: 'Export', data: 'Export' },
                    ],
                  };
                  if (response.response.data_aggregate_type != 'Event') {
                    image_bubble.actions.push({
                      type: 'chat',
                      label: 'Daily average',
                      data: 'Daily average',
                    });
                  }
                  if (response.response.dimension != null) {
                    image_bubble.actions.push({
                      type: 'chat',
                      label: 'Show all',
                      data: 'Show all',
                    });
                  } else {
                    let dimensions = response.response.dimensions;
                    if (dimensions && dimensions.length > 0) {
                      dimensions.forEach(dim => {
                        dim.values.forEach(dimValue => {
                          let data = `for ${dimValue}`;
                          let nlpDimValue = nlp(dimValue);
                          if (
                            nlpDimValue
                              .match(
                                '(#Adjective|#OpenCompound|#ClosedCompound)'
                              )
                              .terms().length > 0
                          ) {
                            data = `for ${dimValue}`;
                            //move to this when we support holding the context
                            //data = `${dimValue} ${response.response.substance}`;
                          } else if (
                            nlpDimValue
                              .match('(#LastName|#FirstName|#Name)')
                              .terms().length > 0
                          ) {
                            data = `for ${dimValue}`;
                          } else if (
                            nlpDimValue.match('#Noun').terms().length > 0
                          ) {
                            data = `of ${dimValue}`;
                          }
                          image_bubble.actions.push({
                            type: 'chat',
                            group: dim.prop,
                            label: data,
                            data: data,
                          });
                        });
                      });
                    }
                  }
                  bubbles.push(image_bubble);
                }
                return bubbles;

              default:
                return this.bubbleCommonResponseHandler(response);
            }
          });

      case 'substance-records':
      case 'substance-export':
        params.push(`verbose=true`);
        params.push(`include_desc=true`);
        return fetch(`/site_assistant/substance.json?${params.join('&')}`)
          .then(response => response.json())
          .then(response => {
            this.state.response_type = response.response_type;
            switch (response.response_type) {
              case 'ok':
                let title = response.response.substance;
                let start = response.response.start;
                let end = response.response.end;
                let date = moment(start, 'YYYY-MM-DD').format(
                  LocalisationTimeFormats().ABy_d
                );
                if (start != end) {
                  date = `${date} to ${moment(end, 'YYYY-MM-DD').format(
                    LocalisationTimeFormats().ABy_d
                  )}`;
                }

                if (this.state.type == 'substance-export') {
                  return [
                    {
                      owner: 'bot',
                      text: `Ok so just confirming you want me to send ${title} data for ${date} to ${response.response.user.email}?`,
                      actions: [
                        { type: 'chat', label: 'Yes', data: 'Yes' },
                        { type: 'chat', label: 'No', data: 'No' },
                      ],
                    },
                  ];
                } else {
                  //pluck out records and sort desc by date
                  let records = Object.keys(response.records)
                    .map(recordKey =>
                      Object.assign(
                        { id: parseInt(recordKey) },
                        response.records[recordKey]
                      )
                    )
                    .sort(
                      (a, b) =>
                        b['date_raised'].localeCompare(a['date_raised']) ||
                        b.id - a.id
                    );
                  let footer = '';
                  if (records.length > 26) {
                    let remaining = records.length - 25;
                    records = records.slice(0, 25);
                    footer = `\n${remaining} records were ommitted. Try a smaller date range or request an export to see them all.`;
                  }
                  let text = records
                    .map(
                      record =>
                        `[${record['date_raised']} - ${record['description']}](${record['href']})`
                    )
                    .join('\n');
                  return [
                    {
                      owner: 'bot',
                      text: `*${title}*\n*${date}*\n${text}${footer}`,
                      actions: [
                        { type: 'chat', label: 'Export', data: 'Export' },
                      ],
                    },
                  ];
                }

              default:
                return this.bubbleCommonResponseHandler(response);
            }
          });

      case 'substance-daily-average':
      case 'substance-monthly-average':
        params.push(`verbose=true`);
        return fetch(`/site_assistant/substance.json?${params.join('&')}`)
          .then(response => response.json())
          .then(response => {
            this.state.response_type = response.response_type;
            switch (response.response_type) {
              case 'ok':
                let isDaily = this.state.type.indexOf('daily') != -1;
                let count = 0;
                if (isDaily) {
                  count = Array.from(
                    new Set(response.data.map(record => record['timestamp']))
                  ).length;
                } else {
                  count = Array.from(
                    new Set(
                      response.data.map(record =>
                        record['timestamp'].substring(0, 7)
                      )
                    )
                  ).length;
                }
                let sum = response.data
                  .map(record => record['value'])
                  .reduce(function(a, b) {
                    return a + b;
                  }, 0);
                let title = 'Daily Average';
                if (!isDaily) {
                  title = 'Monthly Average';
                }
                let dateRange = this.getDateRange(response.response);
                return [
                  {
                    owner: 'bot',
                    text: `*${title}*\n*${
                      dateRange.text
                    }*\n${HasSubstanceBotState.formatSubstance(
                      Object.assign(response.response, { amount: sum / count })
                    )}`,
                  },
                ];

              default:
                return this.bubbleCommonResponseHandler(response);
            }
          });
    }
  }

  getDateRange(response) {
    return getDateRange(response);
  }

  bubbleCommonResponseHandler(response) {
    switch (response.response_type) {
      case 'nodata':
        let dateRange = this.getDateRange(response.response);
        return [
          {
            owner: 'bot',
            text: `There is no ${response.response.substance} data ${dateRange.prefix} ${dateRange.text}.`,
          },
        ];

      default:
        if (response.error_message) {
          return [{ owner: 'bot', text: response.error_message }];
        }
        return [
          {
            owner: 'bot',
            text: `I can't find anything that looks like *${this.state.subject.normal}* to report on. Try rewording the question or ask for help to see ways you can talk to me.`,
          },
        ];
    }
  }
}

function getDateRange(response) {
  if (!response.start) {
    return { text: 'yet', range: false, prefix: '' };
  }
  let start = response.start;
  let end = response.end;
  let date = moment(start, 'YYYY-MM-DD').format(
    LocalisationTimeFormats().AdBy_d
  );
  if (start != end) {
    date = `${date} to ${moment(end, 'YYYY-MM-DD').format(
      LocalisationTimeFormats().AdBy_d
    )}`;
  }
  let range = start != end;
  return { text: date, range: range, prefix: range ? 'between' : 'on' };
}

botStateConstuctors['SummaryBotState'] = (session, state) =>
  new SummaryBotState(session, state);
class SummaryBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    //if there is an identifyable subject transition to this state
    if (
      parsedSentence.subject.success &&
      parsedSentence.subject.normal == 'summary' &&
      parsedSentence.datespan.success
    ) {
      let state = {
        type: 'summary',
        parsed: parsedSentence,
        datespan: parsedSentence.datespan,
      };
      return new SummaryBotState(session, state);
    }
  }

  constructor(session, state) {
    super('SummaryBotState', session, state);
  }

  next(parsedSentence) {
    if (
      parsedSentence.datespan.success &&
      (!parsedSentence.subject.success ||
        parsedSentence.subject.normal == 'summary')
    ) {
      return new SummaryBotState(this.session, {
        type: 'summary',
        parsed: parsedSentence,
        datespan: parsedSentence.datespan,
      });
    }

    return super.next(parsedSentence);
  }

  bubble() {
    let fetch = Container.get('fetch');

    let params = [];
    params.push(`start=${this.state.datespan.start}`);
    params.push(`end=${this.state.datespan.end}`);
    if (this.session.context.project) {
      params.push(`project=${this.session.context.project}`);
    }
    return fetch(`/site_assistant/summary.json?${params.join('&')}`)
      .then(response => response.json())
      .then(response => {
        if (response.error_message) {
          return [{ owner: 'bot', text: response.error_message }];
        }
        if (response.data && response.data.length == 0) {
          let dateRange = getDateRange({
            start: response.requested_start,
            end: response.requested_end,
          });
          return [
            {
              owner: 'bot',
              text: `I have no data to summarize ${dateRange.prefix} ${dateRange.text}.`,
            },
          ];
        }
        return [{ owner: 'bot', text: this.bubbleFormatSummary(response) }];
      });
  }

  bubbleFormatSummary(response) {
    let metricsInfo = ['*Metrics*'];
    let templateInfo = ['*Forms*'];
    response.data.forEach(item => {
      if (item.source === 'ProgressPoint') {
        metricsInfo.push(`${HasSubstanceBotState.formatSubstance(item)}`);
        if (item.dimensions && item.dimensions.length > 0) {
          item.dimensions.forEach(dimension => {
            metricsInfo.push(
              `    ${HasSubstanceBotState.formatSubstance(dimension)}`
            );
          });
        }
      } else if (item.source === 'Template') {
        templateInfo.push(`${HasSubstanceBotState.formatSubstance(item)}`);
      }
    });

    let dateRange = getDateRange({
      start: response.requested_start,
      end: response.requested_end,
    });
    let info = [`**Summary ${dateRange.prefix} ${dateRange.text}**`];
    if (metricsInfo.length > 1) {
      info = info.concat(metricsInfo);
    }
    if (templateInfo.length > 1) {
      if (metricsInfo.length > 1) {
        info.push('');
      }
      info = info.concat(templateInfo);
    }
    return info.join('\n');
  }
}

botStateConstuctors['UnitConversionBotState'] = (session, state) =>
  new UnitConversionBotState(session, state);
class UnitConversionBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    let onlyHasUom =
      parsedSentence.unit.success == true &&
      parsedSentence.subject.success == false &&
      parsedSentence.datespan.success == false &&
      parsedSentence.dimension.success == false;
    if (onlyHasUom && parsedSentence.unit.value) {
      let state = {
        type: 'unit-conversion',
        parsed: parsedSentence,
        from: parsedSentence.unit.value,
        to: parsedSentence.unit.uom,
      };
      return new UnitConversionBotState(session, state);
    }
  }

  constructor(session, state) {
    super('UnitConversionBotState', session, state);
  }

  bubble() {
    let fromValue = parseFloat(this.state.from);
    let fromUnit = this.state.from.replace(/[^a-zA-Z ]/g, '');
    let toUnit = this.state.to;
    let fromUnitConfig = this.findApeUnit(fromUnit);
    let toUnitConfig = this.findApeUnit(toUnit);
    let toValue =
      (fromValue * fromUnitConfig.conversion_to_si_base_unit) /
      toUnitConfig.conversion_to_si_base_unit;
    if (Math.abs(toValue - Math.trunc(toValue)) > 0) {
      let precision = 2;
      while (toValue.toFixed(precision) == 0) {
        precision = precision + 1;
      }
      toValue = toValue.toFixed(precision);
    } else {
      toValue = parseInt(toValue);
    }
    return Promise.resolve([
      {
        owner: 'bot',
        text: `${fromValue}${fromUnitConfig.symbol} is ${toValue}${toUnitConfig.symbol}`,
      },
    ]);
  }

  findApeUnit(unit) {
    let units = this.session.nlpConfig.ape_units;
    return (
      units[unit] ||
      Object.keys(units)
        .map(key => units[key])
        .find(item => item.name == unit || item.singular == unit)
    );
  }
}

const operatorWords = {
  plus: '+',
  add: '+',
  and: '+',
  minus: '-',
  take: '-',
  subtract: '-',
  times: '*',
  'multiplied by': '*',
  divide: '/',
  'divide by': '/',
  divides: '/',
  divided: '/',
  'divided by': '/',
};
const operatorWordsRegex = new RegExp(
  '\\b' + Object.keys(operatorWords).join('\\b|\\b') + '\\b',
  'gi'
);

botStateConstuctors['CalculatorBotState'] = (session, state) =>
  new CalculatorBotState(session, state);
class CalculatorBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    try {
      let expression = parsedSentence.numberParsedSentence;
      expression = expression.replace(
        operatorWordsRegex,
        subPhrase => operatorWords[subPhrase.toLowerCase()]
      );
      expression = expression.replace(/\s/g, '');
      let ast = parse(expression).body[0].expression;
      if (ast.type != 'UnaryExpression' && ast.type != 'Literal') {
        let result = evaluate(ast);
        if (result != null && result != undefined) {
          // if the result is very long then we might have a floating point issue - ie
          // a result that looks like 0.10000000000000009. In this case we round the number
          // to 15 decimal places (1 less than the max for a 64bit float), which should
          // remove this, at the expense of a small amount of precision
          if (result.toString().length > 12) {
            const rnd = (val, dp) =>
              Math.round(val * Math.pow(10, dp)) / Math.pow(10, dp);
            result = rnd(result, 10);
          }

          let state = {
            type: 'calculator',
            parsed: parsedSentence,
            result: `${expression}=${result}`,
          };
          return new CalculatorBotState(session, state);
        }
      }
    } catch (e) {
      console.log(e);
      /* couldnt parse as expression */
    }
  }

  constructor(session, state) {
    super('CalculatorBotState', session, state);
  }

  bubble() {
    return Promise.resolve([{ owner: 'bot', text: `${this.state.result}` }]);
  }
}

botStateConstuctors['HelperBotState'] = (session, state) =>
  new HelperBotState(session, state);
class HelperBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    if (parsedSentence.help === true) {
      let state = { type: 'help', parsed: parsedSentence };
      return new HelperBotState(session, state);
    }
  }

  constructor(session, state) {
    super('HelperBotState', session, state);
  }

  bubble() {
    let progressPoints = this.session.nlpConfig.data_aggregates || [];
    progressPoints.sort();
    let helpText = [];
    helpText.push(
      'Hi I am the Site Assistant, here to help you gain Insights from your data and help out wherever I can.'
    );
    helpText.push('');
    if (progressPoints.length > 0) {
      helpText.push('You can ask me to show data from the following Metrics:');
      helpText.push(progressPoints.map(item => `* ${item}`).join('\n'));
      helpText.push('');
      let examplePp = progressPoints
        .sort((a, b) => a.length - b.length)[0]
        .toLowerCase();
      helpText.push('Here are some examples you might try:');
      helpText.push(`* *${examplePp}* for the last week`);
      helpText.push(`* daily average *${examplePp}* for last month`);
      helpText.push(`* *${examplePp}* records for january`);
      helpText.push('');
    }
    helpText.push('You can ask about Forms in your system:');
    helpText.push(`* how many *template name* last week`);
    helpText.push(`* closed *template name*`);
    helpText.push('');
    helpText.push(
      'You can ask for a summary of Metric totals and form counts:'
    );
    helpText.push(`* summary for yesterday`);
    helpText.push('');
    helpText.push('I can also help out with:');
    helpText.push(`* Unit conversions - 100kg in lb`);
    helpText.push(`* Maths - twenty two divided by seven`);
    helpText.push('');
    helpText.push(
      "That's it for now but I am constantly learning feel free to contact support for more information."
    );
    return Promise.resolve([{ owner: 'bot', text: helpText.join('\n') }]);
  }
}

botStateConstuctors['ProcoreBotState'] = (session, state) =>
  new ProcoreBotState(session, state);
class ProcoreBotState extends BotState {
  static tryCreate(session, parsedSentence) {
    if (parsedSentence.subject.success && parsedSentence.procore === true) {
      let type = ProcoreBotState.typeFromParsedSentence(parsedSentence);
      if (type != null) {
        let state = {
          type: type,
          parsed: parsedSentence,
          subject: parsedSentence.subject,
          dimension: parsedSentence.dimension,
          datespan: parsedSentence.datespan,
        };
        return new ProcoreBotState(session, state);
      }
    }
  }

  static typeFromParsedSentence(parsedSentence) {
    if (parsedSentence.subject.normal.indexOf('budget') != -1) {
      return 'procore-budget';
    }
    if (parsedSentence.subject.normal.indexOf('schedule') != -1) {
      return 'procore-schedule';
    }
    return null;
  }

  constructor(session, state) {
    super('ProcoreBotState', session, state);
  }

  bubble() {
    let fetch = Container.get('fetch');

    let params = [];
    if (this.session.context.project) {
      params.push(`project=${this.session.context.project}`);
    }

    switch (this.state.type) {
      case 'procore-budget':
        let subject = this.state.subject.normal.replace('budget', '').trim();
        if (this.state.dimension.success) {
          params.push(`cost_code=${this.state.dimension.normal} ${subject}`);
        } else {
          params.push(`cost_code=${subject}`);
        }
        return fetch(`/site_assistant/procore.json?${params.join('&')}`, {
          method: 'GET',
        })
          .then(response => response.json())
          .then(response => {
            if (response.ok === true) {
              let text = [
                `*Cost Code: ${response.cost_code}*`,
                `*Cost Type: ${response.category}*`,
                `Projected Budget: ${this.formatCurrency(
                  response.projected_budget
                )}`,
                `Job To Date Cost: ${this.formatCurrency(response.jtd_cost)}`,
                `Est Cost at Completion: ${this.formatCurrency(
                  response.completion_budget
                )}`,
                `Projected over Under: ${this.formatCurrency(
                  response.delta_budget
                )}`,
              ];
              let actions = [];
              if (
                response.cost_code != 'Project' ||
                response.category != 'Multiple'
              ) {
                actions.push({
                  type: 'chat',
                  label: 'Project Budget',
                  data: `Procore budget`,
                });
              }
              if (response.cost_codes.length > 1) {
                response.cost_codes.forEach(cost_code => {
                  let parts = [];
                  parts.push(cost_code);
                  if (response.category != 'Multiple') {
                    parts.push(response.category);
                  }
                  actions.push({
                    type: 'chat',
                    group: 'Cost Code',
                    label: cost_code,
                    data: `Procore ${parts.join(' ')} budget`,
                  });
                });
              }
              if (response.categories.length > 1) {
                response.categories.forEach(category => {
                  let parts = [];
                  if (response.cost_code != 'Project') {
                    parts.push(response.cost_code);
                  }
                  parts.push(category);
                  actions.push({
                    type: 'chat',
                    group: 'Cost Type',
                    label: category,
                    data: `Procore ${parts.join(' ')} budget`,
                  });
                });
              }
              return [
                { owner: 'bot', text: text.join('\n'), actions: actions },
              ];
            }
            if (response.error) {
              return [{ owner: 'bot', text: response.error }];
            }
            return [
              {
                owner: 'bot',
                text: 'Something went wrong could you pleae try again.',
              },
            ];
          });

      case 'procore-schedule': {
        let subject = this.state.subject.normal.replace('schedule', '').trim();
        if (this.state.dimension.success) {
          params.push(`resource=${this.state.dimension.normal} ${subject}`);
        } else {
          params.push(`resource=${subject}`);
        }
        if (this.state.datespan.success) {
          params.push(`start=${this.state.datespan.start}`);
          params.push(`end=${this.state.datespan.end}`);
        }
        return fetch(
          `/site_assistant/procore_schedule.json?${params.join('&')}`,
          { method: 'GET' }
        )
          .then(response => response.json())
          .then(response => {
            if (response.ok === true) {
              if (response.tasks.length + response.todos.length == 0) {
                return [{ owner: 'bot', text: 'There are no schedule items.' }];
              }

              let formatItem = item => [
                `${item.name}`,
                `  ${getDateRange(item).text}`,
                `  Completion: ${parseInt(item.percentage)}%`,
                `  Resource: ${item.resource}`,
              ];

              let text = [];
              this.formatItems(
                text,
                'Tasks',
                response.tasks,
                formatItem,
                10,
                'Try a smaller date range or filter on resource.'
              );
              if (text.length > 0) {
                text.push('');
              }
              this.formatItems(
                text,
                'Todos',
                response.todos,
                formatItem,
                10,
                'Try a smaller date range or filter on resource.'
              );

              let actions = [];
              if (response.resources.length > 1) {
                let datetext = this.state.datespan.success
                  ? ` for ${this.state.datespan.normal}`
                  : '';
                response.resources.forEach(resource => {
                  actions.push({
                    type: 'chat',
                    group: 'Resource',
                    label: resource,
                    data: `Procore ${resource} schedule${datetext}`,
                  });
                });
              }

              return [
                { owner: 'bot', text: text.join('\n'), actions: actions },
              ];
            }
            if (response.error) {
              return [{ owner: 'bot', text: response.error }];
            }
            return [
              {
                owner: 'bot',
                text: 'Something went wrong could you pleae try again.',
              },
            ];
          });
      }
    }
  }

  formatCurrency(value) {
    if (value < 0) {
      return `$(${this.formatNumber(Math.abs(value))})`;
    }
    return `$${this.formatNumber(value)}`;
  }

  formatNumber(value) {
    let parts = value.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  formatItems(stringBuffer, title, items, formatter, limit, ommittedText) {
    let footer = null;
    if (items.length > limit + 1) {
      let remaining = items.length - limit;
      items = items.slice(0, limit);
      footer = `${remaining} ${title} were ommitted. ${ommittedText}`;
    }
    if (items.length > 0) {
      stringBuffer.push(`**${title}**`);
      items.forEach(item =>
        formatter(item).forEach(line => stringBuffer.push(line))
      );
    }
    if (footer != null) {
      stringBuffer.push(footer);
    }
  }
}
